import React, { PureComponent } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { ISkin as IDashboardSkin } from '../dashboard/Dashboard'
import Leaderboard, { ILeaderboardSkin } from '../leaderboard/Leaderboard'
import Countdown, { ISkin as ICountdownSkin } from '../countdown/Countdown'
import { ISkin as IQuestionSkin } from '../question/Question'
import { IMountainPlayerData } from '../../helpers/data/mountainPlayer'
import { IAudioData } from '../../helpers/data/audioData'
import { ISize } from '../../helpers/data/size'
import { IPlayerData, IPlayerWithCurrentNumberOfCorrectAnswer } from '../../helpers/data/player'
import { IGameActivity } from '../../helpers/data/question'
import minQueryHelper from '../../helpers/minQueryHelper'
import getPalette, { IPalette } from '../../helpers/colors/colors'
import GameTooltip from './components/GameTooltip'
import AudioElement from '../../components/audio/AudioElement'
import ImagePreview from '../../components/imagePreview/ImagePreview'
import Timer from '../../components/timer/Timer'
import RichTextContent from '../../components/richText/RichTextContent'
import getFont from '../../helpers/fonts/fonts'
import timings from '../../helpers/timings/timings'
import { ReactComponent as StartIcon } from '../../assets/icons/start-button.svg'
import MainButton from '../../components/buttons/MainButton'
import { ReactComponent as CorrectAnswer } from '../../assets/icons/correct-answer.svg'

import { ReactComponent as QuestionMarkIcon } from '../../assets/icons/question-mark-circle.svg'
import expandIcon from '../../assets/icons/expand-icon.svg'
import ClassPerformance from './components/ClassPerformance'
import VolumeBar from './components/VolumeControl'
import settingsEnums from '../../helpers/enums/settings'
const palette: IPalette = getPalette()

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	margin: auto;
	max-width: 1024px;
	max-height: 768px;
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	@media (max-width: ${minQueryHelper}px) {
		max-height: none;
	}
`

const GameContainer = styled.div`
	height: calc(100% - 68px);
	display: block;
	border-right: 1px solid ${palette.blackDark};
	border-bottom: 1px solid ${palette.blackDark};

	@media (max-width: 600px) {
		width: 100%;
	}

	@media (min-width: 600px) {
		display: flex;
	}
`

const LeaderboardContainer = styled.div`
	height: 100%;
	flex: 1 1 0;
	background-color: rgba(127, 127, 127, 1);
	vertical-align: top;
	max-width: 50%;

	@media (max-width: 600px) {
		width: 100%;
		max-width: 100%;
	}
`

const DashboardContainer = styled.div`
	height: 100%;
	flex: 1 1 0;
	display: inline-flex;
	flex-direction: column;
	justify-content: space-between;
	vertical-align: top;
	overflow-y: scroll;
	overflow-x: hidden;
	position: relative;
	font-family: ${getFont('primaryMedium')};
	color: ${palette.darkBlue};

	@media (max-width: 600px) {
		display: none;
	}
`

const CountdownContainer = styled.div`
	width: 100%;
	height: 100%;
	background-color: white;
	position: absolute;
	top: 0;
	left: 0;
`
const GameTimerContainer = styled.div`
	width: 60px;
	height: 60px;
	position: absolute;
	top: 78px;

	//small devices like iphone 4
	@media (max-width: 300px) {
		right: 5px !important;
	}

	@media (max-width: 600px) {
		top: 13px;
	}
`

const QuestionHeader = styled.div`
	width: 100%;
	height: 68px;
	color: rgb(255, 255, 255);
	font-size: 28px;
	line-height: 68px;
	text-align: center;
	font-family: ${getFont('primaryMedium')};
	background-color: ${palette.blue};
`

const progressBarIn = keyframes`
  0% {
    transform: scale(0, 1);
    animation-timing-function: ease-out;
  }
  100% {
    transform: scale(1, 1);
  }
`

const QuestionProgressBar = styled.div<{ isPauseSettingEnable?: boolean }>`
	width: 100%;
	height: 24px;
	animation: ${progressBarIn} ${timings.questionTransition}s;
	animation: ${progressBarIn} ${(props) => (props.isPauseSettingEnable ? '8s' : timings.questionTransition + 's')};
	animation-delay: ${(props) => (props.isPauseSettingEnable ? '0s' : timings.questionReveal + 's')};
	animation-fill-mode: both;
	transform-origin: 0 0;
	position: absolute;
	left: 0;
	z-index: 299;
	background-color: ${palette.blue};
	bottom: 56px;
`

const QuestionContainer = styled.div`
	max-width: 91%;
	background-image: linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
	padding: 16px 20px 16px 24px;

	@media (min-width: 1100px) {
		max-width: 90%;
	}
`

interface IAnswersContainer {
	answerType?: string
}

const AnswersContainer = styled.ol<IAnswersContainer>`
	clear: both;
	list-style: none;
	padding-top: 8px;
	padding-left: 25px;
	min-height: 80px;

	${(props) =>
		props.answerType === 'image' &&
		css`
			display: flex;
			flex-direction: column;
		`}
`

const IndividualAnswer = styled.div`
	position: relative;
	padding-top: 8px;
`

const ContentAnswer = styled.li<IAnswersContainer>`
	line-height: 1.2;
	display: flex;
	justify-content: flex-start;

	align-items: ${(props) => (props.answerType === 'image' ? 'flex-start' : 'baseline')};
`

const ImageAnswer = styled.img`
	width: 72px;
	height: 56px;
	max-width: 100%;
	object-fit: contain;
	object-position: center;
`

const BulletAnswer = styled.span`
	padding-right: 8px;
	position: relative;
`

// Marks the correct answer, in the gutter left of the bullet. Anchored to the bullet rather than to the
// answer row: a row holding a formula is taller than its own text, so a row-relative offset drifts off
// the text it marks. Centred on the bullet's line box, which the icon is short enough to sit inside.
const CorrectAnswerIcon = styled(CorrectAnswer)`
	position: absolute;
	left: -24px;
	top: 50%;
	transform: translateY(-50%);
	color: ${palette.success};
`

const ChartContainer = styled.div`
	padding: 5px 0px 45px;
`

const QuestionLabel = styled.div`
	width: 100%;
	font-family: ${getFont('primaryMedium')};
	font-size: 20px;
	padding-bottom: 16px;
`
const IconContainer = styled.div`
	padding-right: 20px;
	padding-left: 20px;
	position: relative;
	max-width: 100%;
	max-height: 100%;
	float: left;
	margin-right: 10px;
	border: 1px solid ${palette.darkBlue};
	background-color: ${palette.darkBlue};
	cursor: pointer;
`

const Icon = styled.img`
	position: absolute;
	bottom: 0;
	right: 0;
`
const QuestionImage = styled.img`
	max-width: 263px;
	max-height: 180px;
	float: left;

	&.with-text {
		max-width: 105px;
		max-height: 70px;
	}

	@media screen and (max-width: 920px) {
		&:not(.with-text) {
			max-width: 90%;
		}
	}
`

const QuestionText = styled.div`
	width: 100%;
	font-family: ${getFont('primaryMedium')};
	font-size: 16px;
`
const ClassPerformanceTitle = styled.h2`
	padding: 0px 7px 0px 24px;
`

const ClassPerformanceTitleAndSvgContainer = styled.div`
	display: flex;
`

const WaitingBar = styled.div`
	width: 100%;
	height: 56px;
	padding: 12px 16px;
	background-color: rgb(0, 87, 133);
	color: rgba(255, 255, 255, 1);
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 7px;
	position: absolute;
	bottom: 0;
	@media (min-width: 600px) {
		font-size: 16px;
	}

	@media (min-width: 1500px) {
		font-size: 20px;
	}
`

const MainButtonContainer = styled.div`
	flex: 0 1 0;
	height: 100%;
	font-size: 14px;
	display: flex;
	justify-content: center;
	align-items: center;
	padding-right: 8px;

	@media (min-width: 600px) {
		padding-right: 16px;
	}
`
const scaleAnimationIn = keyframes`
  0% {
    transform: scale(0, 1);
    animation-timing-function: ease-out;
  }
  100% {
    transform: scale(1, 1);
  }
`

const QuestionHeaderPausedText = styled.div`
	position: relative;
	top: -55px;
	animation: ${scaleAnimationIn} 1s;
	animation-delay: 1s;
	animation-fill-mode: both;
`

const svgStyles = {
	width: '16px',
	height: '16px',
	marginTop: '3px',
}

const mainButtonCustomCss = css`
	min-width: 120px;
	min-height: 34px;
	@media (min-width: 600px) {
		min-width: 150px;
		min-height: 34px;
	}
	@media (min-width: 850px) {
		min-width: 150px;
		min-height: 40px;
	}
	@media (min-width: 1400px) {
		min-width: 155px;
		padding: 2px 16px;
	}
	@media (min-width: 1500px) {
		min-width: 155px;
	}
`

export interface ISkin {
	leaderboard: ILeaderboardSkin
	audio: IAudioData
	dashboard: IDashboardSkin
	countdown: ICountdownSkin
	questions: IQuestionSkin
	progressBar: IProgressBarSkin
}

interface IProgressBarSkin {
	color: string
}

interface IGameProps {
	isFinished: boolean
	playerData: IPlayerData
	players: IMountainPlayerData[]
	activity: IGameActivity
	totalActivities: number
	totalTime: number
	size: ISize
	elapsedTime: number
	skin: ISkin
	shouldHideStudentNames: boolean
	getCurrentNumberOfCorrectAnswersByParticipants: () => void
	moveToNextActivity: () => void
	audioUpdate: (volume: number, isMuted: boolean) => void
	isPauseEnabledSetting: boolean
	isTimerDisabledSetting: number
	studentsIDWithNumberOfCorrectAnswersFromServer: IPlayerWithCurrentNumberOfCorrectAnswer[] | undefined
	volume: number
	isMuted: boolean
	gameMode: number
}

interface IGameState {
	initialTime: number
	currentTime: number
	countdownComplete: boolean
	isImagePreviewOpen: boolean
	showProgressBar: boolean
}

class Game extends PureComponent<IGameProps, IGameState> {
	private elapsedTime = 0

	state = {
		initialTime: 0,
		currentTime: 0,
		countdownComplete: false,
		isImagePreviewOpen: false,
		showProgressBar: false,
		volume: 0.2,
		isMuted: false,
	}

	componentDidUpdate(prevProps: IGameProps) {
		if (prevProps.activity.uid !== this.props.activity.uid) {
			this.setState({
				initialTime: 0,
				currentTime: 0,
				countdownComplete: false,
				isImagePreviewOpen: false,
				showProgressBar: false,
			})
		}
	}

	onComplete = () => {
		this.setState({
			initialTime: Date.now(),
			countdownComplete: true,
		})
	}

	onTimeUpdate = (elapsedTime: number) => {
		//TODO: decide how to store this updated value
		this.elapsedTime = elapsedTime
	}

	onVolumeChange = (volume: any) => {
		this.props.audioUpdate(volume, false)
	}

	onMuteSelected = (muteNewStatus: boolean) => {
		this.props.audioUpdate(this.props.volume, muteNewStatus)
	}

	getGameSize(windowSize: ISize) {
		let gameWidth
		let questionWidth
		let gameHeight
		let dashboardWidth, dashboardHeight, scale
		if (windowSize.width < minQueryHelper) {
			return {
				gamewidth: '100%',
				gameHeight: '100%',
				dashboardWidth: '50%',
				dashboardHeight: '100%',
				questionWidth: '100%',
				scale: 1,
				timerScale: 0.7,
				timerRight: '13px',
			}
		} else {
			const originalWidth = 512
			const originalHeight = 700
			const containerHeight = Math.min(windowSize.height - 68, originalHeight)
			const containerWidth = Math.min(windowSize.width - 512, originalWidth)
			const containerRel = containerWidth / containerHeight
			const originalRel = originalWidth / originalHeight
			if (originalRel > containerRel) {
				dashboardHeight = (originalHeight * containerWidth) / originalWidth
				dashboardWidth = (dashboardHeight * originalWidth) / originalHeight
			} else {
				dashboardWidth = (originalWidth * containerHeight) / originalHeight
				dashboardHeight = (dashboardWidth * originalHeight) / originalWidth
			}
			gameWidth = Math.min(1024, windowSize.width)
			gameHeight = dashboardHeight + 68
			questionWidth = gameWidth - dashboardWidth
			scale = dashboardWidth / originalWidth
		}
		return {
			gamewidth: gameWidth + 'px',
			dashboardWidth: dashboardWidth + 'px',
			dashboardHeight: dashboardHeight + 'px',
			gameHeight: gameHeight + 'px',
			questionWidth: questionWidth + 'px',
			scale,
			timerScale: scale,
			timerRight: 27 * scale + 'px',
		}
	}

	toggleImagePreview = () => {
		this.setState({
			isImagePreviewOpen: !this.state.isImagePreviewOpen,
		})
	}

	handleSubmit = (ev: React.FormEvent) => {
		this.setState({ showProgressBar: true })
		this.props.moveToNextActivity()
		ev.stopPropagation()
		ev.preventDefault()
	}

	render() {
		const croppingWidth = 'calc(100% - 245px)'

		const gameSize = this.getGameSize(this.props.size)
		const audioPath = this.props.skin.audio ? this.props.skin.audio.path : 'audios/gameplay.mp3'

		const shouldPauseGame =
			this.props.isPauseEnabledSetting ||
			this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_SESSION ||
			this.props.isTimerDisabledSetting === settingsEnums.SETTING_TIMER_DISABLED_FROM_CREATION

		const isGamePaused = shouldPauseGame && this.props.isFinished

		const shouldShowTimer =
			this.state.countdownComplete &&
			this.props.isTimerDisabledSetting !== settingsEnums.SETTING_TIMER_DISABLED_FROM_SESSION &&
			this.props.isTimerDisabledSetting !== settingsEnums.SETTING_TIMER_DISABLED_FROM_CREATION

		const isNextQuestionButtonDisabled =
			!this.props.isFinished || !this.state.countdownComplete || this.state.showProgressBar

		return (
			<Wrapper style={{ width: gameSize.gamewidth, height: gameSize.gameHeight }}>
				<QuestionHeader
					style={{
						backgroundColor: isGamePaused ? palette.grayLight2 : palette.blue,
						transition: isGamePaused ? 'background-color 1000ms linear 1s' : 'none',
					}}
				>
					<p
						style={{
							transform: isGamePaused ? 'scale(0.5)' : 'scale(1)',
							position: 'relative',
							top: isGamePaused ? '-13px' : '0',
							transition: isGamePaused ? 'all 1000ms linear 1s' : 'none',
						}}
					>
						{`Time to Climb`}
					</p>

					{isGamePaused && <QuestionHeaderPausedText>Paused</QuestionHeaderPausedText>}
				</QuestionHeader>

				<GameContainer>
					<LeaderboardContainer>
						<Leaderboard
							croppingWidth={croppingWidth}
							players={this.props.players}
							skin={this.props.skin.leaderboard}
							totalActivities={this.props.totalActivities}
							currentActivity={this.props.activity.index + 1}
							shouldShowCorrectAnswers={true}
							getCurrentNumberOfCorrectAnswersByParticipants={this.props.getCurrentNumberOfCorrectAnswersByParticipants}
							studentsIDWithNumberOfCorrectAnswersFromServer={this.props.studentsIDWithNumberOfCorrectAnswersFromServer}
						/>
					</LeaderboardContainer>

					<DashboardContainer style={{ height: gameSize.dashboardHeight }}>
						<QuestionContainer>
							{/* Question x/total */}
							<QuestionLabel>
								Question {this.props.activity.index + 1}/{this.props.totalActivities}
							</QuestionLabel>

							{/* Question reference Image  */}
							{this.props.activity.payload.asset && (
								<>
									<IconContainer onClick={this.toggleImagePreview}>
										<QuestionImage
											src={this.props.activity.payload.asset.source}
											alt={
												!this.props.activity.payload.asset.isDecorative
													? this.props.activity.payload.asset.altText || 'Reference Image'
													: ''
											}
											aria-hidden={this.props.activity.payload.asset.isDecorative}
											className={!!this.props.activity.payload.text ? 'with-text' : ''}
										/>
										<Icon src={expandIcon} />
									</IconContainer>
								</>
							)}

							{/* Question text  */}
							{this.props.activity.payload.text && (
								<RichTextContent
									as={QuestionText}
									content={this.props.activity.payload.text}
									speechOutlet='hiddenText'
									formulaWeight='light'
								/>
							)}

							{/* Answers */}
							<AnswersContainer type='A' answerType={this.props.activity.payload.answers[0].media.type}>
								{this.props.activity.payload.answers.map((answer, index) => {
									return (
										<IndividualAnswer key={answer.uid}>
											<ContentAnswer answerType={answer.media.type}>
												<BulletAnswer>
													{this.props.isFinished && !!answer.correct && <CorrectAnswerIcon />}
													{String.fromCharCode(65 + index)}.{' '}
												</BulletAnswer>

												{answer.media.type === 'text' && (
													<RichTextContent
														as='span'
														content={answer.media.payload.text}
														speechOutlet='hiddenText'
														formulaWeight='light'
													/>
												)}
												{answer.media.type === 'image' && <ImageAnswer src={answer.media.payload.source} />}
											</ContentAnswer>
										</IndividualAnswer>
									)
								})}
							</AnswersContainer>
						</QuestionContainer>

						<ChartContainer>
							<ClassPerformanceTitleAndSvgContainer>
								<ClassPerformanceTitle> Class performance </ClassPerformanceTitle>
								{this.props.activity.payload.isRandomized && this.state.countdownComplete && (
									<>
										<QuestionMarkIcon style={svgStyles} data-tooltip-id='questionIconId'></QuestionMarkIcon>
										<GameTooltip id='questionIconId'>
											<span>Answers in this game are randomized for players. </span>
										</GameTooltip>
									</>
								)}
							</ClassPerformanceTitleAndSvgContainer>
							<br />
							<div>
								<ClassPerformance
									players={this.props.players}
									activity={this.props.activity}
									areAnswersHidden={!this.props.isFinished}
								></ClassPerformance>
							</div>
						</ChartContainer>
					</DashboardContainer>

					{shouldShowTimer && (
						<GameTimerContainer
							key={this.props.activity.index}
							style={{
								width: `${60 * gameSize.timerScale}px`,
								height: `${60 * gameSize.timerScale}px`,
								fontSize: `${30 * gameSize.timerScale}px`,
								lineHeight: `${60 * gameSize.timerScale}px`,
								right: gameSize.timerRight,
							}}
						>
							<Timer
								totalTime={this.props.totalTime}
								elapsedTime={this.props.elapsedTime}
								onTimeUpdate={this.onTimeUpdate}
							/>
						</GameTimerContainer>
					)}
				</GameContainer>

				{!this.state.countdownComplete && (
					<CountdownContainer>
						<Countdown key={this.props.activity.index} onComplete={this.onComplete} skin={this.props.skin.countdown} />
					</CountdownContainer>
				)}

				{!!shouldPauseGame && !!this.state.showProgressBar && (
					<QuestionProgressBar isPauseSettingEnable={shouldPauseGame} />
				)}

				{!shouldPauseGame && this.props.isFinished && <QuestionProgressBar />}

				{this.state.isImagePreviewOpen && this.props.activity.payload.asset && (
					<ImagePreview onClose={this.toggleImagePreview} asset={this.props.activity.payload.asset} />
				)}
				<AudioElement src={audioPath} />
				{!!this.state.countdownComplete && (
					<WaitingBar
						style={{
							backgroundColor: `${palette.blackDark}`,
							borderTop: '1px solid white',
						}}
					>
						<VolumeBar
							isMuted={this.props.isMuted}
							onMuteSelected={this.onMuteSelected}
							onVolumeChange={this.onVolumeChange}
							volume={this.props.volume}
							widthSize={this.props.size.width}
							isTransparent={true}
						></VolumeBar>
						{!!shouldPauseGame && (
							<MainButtonContainer
								style={{
									visibility: 'visible',
								}}
							>
								<MainButton
									text={'Next Question'}
									onSubmit={this.handleSubmit}
									isDisabled={isNextQuestionButtonDisabled}
									isRounded={true}
									backgroundColor={palette.radiantBlue}
									Icon={<StartIcon />}
									customCss={mainButtonCustomCss}
								/>
							</MainButtonContainer>
						)}
					</WaitingBar>
				)}
			</Wrapper>
		)
	}
}

export default Game
